import {
  Box,
  Center,
  Flex,
  Switch,
  Tooltip,
  useDisclosure,
} from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import React, { memo, useEffect, useState } from 'react';
import { Edit05 } from '@kula-ai/icons-core';
import { CollapseSection } from '@/components/ats/CollapseSection/CollapseSection';
import { aiScoringApi } from '../api/aiScoring.api';
import { AiScoringConstants } from '../aiScoringConstants';
import { AiScoringJobInfoList } from './components';
import { KText } from '@/ui-library/components';
import { AiScoringDrawer } from '../AiScoringDrawer';
import { AiScoreInterfaces } from '../AiScoreInterface';
import { FormikProps } from 'formik';
import { CenterSpinner } from '@/components/ats';
import { AiButton } from '@kula-ai/components';
import { useAtsPermissions } from '@/hooks/useAtsPermissions';
import { useKFlags } from '@/hooks/use-k-flags';
import { aiScoringKeys } from '@/api/aiScoring/aiScoringKeys';

type JobData = {
  id: string;
  description: {
    text: string | null;
  };
  status: string;
  job_status: string;
};

export const AiScoringSection: React.FC<{
  jobData: JobData;
  jobFormRef: React.RefObject<FormikProps<any>>;
}> = memo(({ jobData, jobFormRef }) => {
  const { jobPermissions } = useAtsPermissions();
  const { feJobTeammates } = useKFlags();
  const isNotJobUpdateAllowedStatus = !['closed', 'archived']?.includes(
    jobData?.job_status
  );

  // ENG-3735: the key must carry the job id — a bare key made every job share
  // one cache entry, so the previously-viewed job's criteria showed up here.
  const { data: { data: aiScoringResponse } = {}, isLoading } = useQuery({
    queryKey: aiScoringKeys.detail(jobData?.id),
    queryFn: () => aiScoringApi.fetchAiScoring(jobData?.id || ''),
    staleTime: 3000,
    enabled: !!jobData?.id,
  });

  const aiSetupDisclosure = useDisclosure();

  const {
    data: {
      ai_scoring: aiScoringResponseData = {},
      bulk_review_progress = {},
    } = {},
  } = aiScoringResponse ?? {};

  const isBulkProgress = bulk_review_progress?.status === 'in_progress';

  // Handle API errors

  const {
    enabled: isAiSetupEnabled,
    attributes,
    auto_reject,
    auto_reject_threshold,
  } = aiScoringResponseData;

  const isAiSetupDone = attributes?.length > 0;

  const [isAiScoringEnabled, setIsAiScoringEnabled] =
    useState(isAiSetupEnabled);
  const [autoRejectEnabledState, setAutoRejectEnabledState] =
    useState(auto_reject);
  const [autoRejectThresholdState, setAutoRejectThresholdState] = useState(
    auto_reject_threshold ?? AiScoringConstants.DEFAULT_AUTO_REJECT_THRESHOLD
  );

  useEffect(() => {
    if (!aiScoringResponseData) return;

    const { enabled, auto_reject, auto_reject_threshold } =
      aiScoringResponseData ?? {};

    setIsAiScoringEnabled((prev) => {
      const newValue = prev !== enabled ? enabled : prev;
      if (prev !== newValue) {
        // Sync form field with API data
        jobFormRef.current?.setFieldValue('aiScoreToggle', newValue);
      }
      return newValue;
    });

    setAutoRejectEnabledState((prev) => {
      const newValue = prev !== auto_reject ? auto_reject : prev;
      if (prev !== newValue) {
        // Sync form field with API data
        jobFormRef.current?.setFieldValue('auto_reject', newValue);
      }
      return newValue;
    });

    setAutoRejectThresholdState((prev) => {
      const apiThreshold =
        auto_reject_threshold ??
        AiScoringConstants.DEFAULT_AUTO_REJECT_THRESHOLD;
      const newValue = prev !== apiThreshold ? apiThreshold : prev;
      if (prev !== newValue) {
        // Sync form field with API data
        jobFormRef.current?.setFieldValue('auto_reject_threshold', newValue);
      }
      return newValue;
    });
  }, [aiScoringResponseData, jobFormRef]);

  const openAiSetup = () => {
    aiSetupDisclosure.onOpen();
  };

  const headerRightContent = (
    <Flex
      gap="12px"
      alignItems="center"
      p="8px"
      pr="0"
      cursor={isNotJobUpdateAllowedStatus ? 'default' : 'not-allowed'}
      onClick={(e) => e.stopPropagation()}
    >
      <Tooltip
        label={
          !jobPermissions?.update
            ? undefined
            : !isNotJobUpdateAllowedStatus
              ? 'Cannot edit attributes for closed or archived jobs'
              : isBulkProgress
                ? 'AI scoring settings are disabled while candidate scores are being calculated'
                : isAiScoringEnabled
                  ? undefined
                  : 'Enable AI scoring to edit the attributes'
        }
        hasArrow
        placement="top"
      >
        <Center>
          <Edit05
            size={18}
            className="kula-icon"
            style={{
              cursor:
                isAiScoringEnabled &&
                jobPermissions?.update &&
                isNotJobUpdateAllowedStatus &&
                !isBulkProgress
                  ? 'pointer'
                  : 'not-allowed',
              opacity: jobPermissions?.update ? 1 : 0.5,
            }}
            onClick={(e) => {
              e.stopPropagation();
              if (
                !jobPermissions?.update ||
                !isNotJobUpdateAllowedStatus ||
                !isAiScoringEnabled ||
                isBulkProgress
              )
                return;
              openAiSetup();
            }}
          />
        </Center>
      </Tooltip>
      <Tooltip
        label={
          !jobPermissions?.update
            ? undefined
            : isBulkProgress
              ? 'AI scoring settings are disabled while candidate scores are being calculated'
              : undefined
        }
        hasArrow
        placement="top"
      >
        <Box opacity={jobPermissions?.update ? 1 : 0.5}>
          <Switch
            className={`ats-switch-toogle ${
              isAiScoringEnabled && !isBulkProgress ? 'active' : 'unactive'
            }`}
            cursor={
              jobPermissions?.update &&
              isNotJobUpdateAllowedStatus &&
              !isBulkProgress
                ? 'pointer'
                : 'not-allowed'
            }
            isChecked={isAiScoringEnabled}
            onChange={(e) => {
              e.stopPropagation();
              if (
                !jobPermissions?.update ||
                !isNotJobUpdateAllowedStatus ||
                isBulkProgress
              )
                return;
              setIsAiScoringEnabled((prev) => {
                const newValue = !prev;
                jobFormRef.current?.setFieldValue('aiScoreToggle', newValue);
                return newValue;
              });
            }}
          />
        </Box>
      </Tooltip>
    </Flex>
  );

  const getSliderColor = (value: number) => {
    const { thresholdMap } = AiScoringConstants;
    if (value <= thresholdMap.low.to) return thresholdMap.low.bg;
    if (value <= thresholdMap.average.to) return thresholdMap.average.bg;
    return thresholdMap.high.bg;
  };

  const renderSetupSection = () => {
    return (
      <Box m="9px" borderRadius="6px" bg="#F9F5FF">
        <Flex p="16px 24px" gap="40px" alignItems="center">
          <Flex flexDir="column" gap="8px">
            <KText variant="heading_4">
              {feJobTeammates
                ? 'Rank candidates with the Scoring agent'
                : 'Rank candidates with AI scoring'}
            </KText>
            <KText color="secondary-900">
              {feJobTeammates
                ? 'Accelerate your screening process with the Scoring agent. Set it up by defining your ideal candidate or using the job description.'
                : 'Accelerate your screening process with AI scoring. Set it up by defining your ideal candidate or using the job description.'}
            </KText>
          </Flex>
          <AiButton
            onClick={openAiSetup}
            disabled={!jobPermissions?.update || !isNotJobUpdateAllowedStatus}
          >
            {feJobTeammates ? 'Set up Scoring agent' : 'Set up AI scoring'}
          </AiButton>
        </Flex>
      </Box>
    );
  };

  if (isLoading) {
    return (
      <Box borderRadius="6px" bg="#F9F5FF">
        <CenterSpinner h="100px" />
      </Box>
    );
  }

  return (
    <Flex w="100%">
      <CollapseSection
        w="100%"
        isV2
        title="AI scoring"
        defaultIsOpen
        headerRightContent={isAiSetupDone ? headerRightContent : null}
      >
        {isAiSetupDone ? (
          <Box position="relative">
            <AiScoringJobInfoList
              attributes={attributes as AiScoreInterfaces.AiScoreApiAttribute[]}
              autoRejectEnabledState={autoRejectEnabledState}
              setAutoRejectEnabledState={(value) => {
                setAutoRejectEnabledState(value);
                jobFormRef.current?.setFieldValue('auto_reject', value);
              }}
              autoRejectThresholdState={autoRejectThresholdState}
              setAutoRejectThresholdState={(value) => {
                setAutoRejectThresholdState(value);
                jobFormRef.current?.setFieldValue(
                  'auto_reject_threshold',
                  value
                );
              }}
              getSliderColor={getSliderColor}
              thresholdMap={AiScoringConstants.thresholdMap}
            />
            {(!isAiScoringEnabled ||
              !jobPermissions?.update ||
              !isNotJobUpdateAllowedStatus ||
              isBulkProgress) && (
              <Box
                position="absolute"
                top="0"
                right="0"
                w="100%"
                h="100%"
                bg="base-white"
                cursor="not-allowed"
                opacity={0.5}
                zIndex={1}
              ></Box>
            )}
          </Box>
        ) : (
          renderSetupSection()
        )}
      </CollapseSection>
      {aiSetupDisclosure.isOpen && (
        <AiScoringDrawer
          jobData={{
            id: jobData.id,
            description: jobData.description,
            status: jobData.status,
            job_status: jobData.job_status,
            ...jobData,
          }}
          drawerDisclosure={aiSetupDisclosure}
          aiScoringData={aiScoringResponseData}
        />
      )}
    </Flex>
  );
});

AiScoringSection.displayName = 'AiScoringSection';
